import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'motion/react';
import { SectionContent, getContentResponse } from '../../Services/UserProjectService';
import { useContentVariation, ProjectTreatment } from '../../hooks/useContentVariation';
import { uploadImage } from '../../Services/HandDoesntLieChallengeService';
import {
  getImageCommentItems,
  saveImageCommentItem,
  deleteImageCommentItem
} from '../../Services/ImageCommentItemService';
import AudioTextInput from './AudioTextInput';

interface ImageCommentViewerProps {
  content: SectionContent;
  project?: ProjectTreatment;
  projectId?: string;
  onResponseChange?: (isValid: boolean, response: string) => void;
  navigationDirection?: 'forward' | 'backward';
}

interface LocalItem {
  image_comment_item_id: string;
  image_url: string;
  comment: string;
  position: number;
  isNew?: boolean;
  isSaving?: boolean;
}

type ViewPhase = 'gallery' | 'comment';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp';

const ImageCommentViewer: React.FC<ImageCommentViewerProps> = ({
  content,
  project,
  projectId,
  onResponseChange,
  navigationDirection = 'forward'
}) => {
  const [items, setItems] = useState<LocalItem[]>([]);
  const [maxImages] = useState(20);
  const [phase, setPhase] = useState<ViewPhase>('gallery');
  const [commentIndex, setCommentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});
  const itemsRef = useRef<LocalItem[]>([]);
  const { getVariationText } = useContentVariation(project);
  const migratedRef = useRef(false);
  const thumbnailStripRef = useRef<HTMLDivElement>(null);

  const contentText = getVariationText(content);
  const contentId = content.id;

  // Load items on mount
  useEffect(() => {
    const loadItems = async () => {
      if (!contentId) return;
      setIsLoading(true);
      try {
        const response = await getImageCommentItems(contentId);

        if (response.data.length > 0) {
          setItems(response.data.map(item => ({
            image_comment_item_id: item.image_comment_item_id,
            image_url: item.image_url,
            comment: item.comment,
            position: item.position,
          })));
        } else if (content.isCompleted && !migratedRef.current) {
          migratedRef.current = true;
          await migrateOldData();
        }
      } catch (error) {
        console.error('Error loading image comment items:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadItems();
  }, [contentId]);

  // Lazy migration from old single-image format
  const migrateOldData = async () => {
    if (!contentId) return;
    try {
      const savedResponse = await getContentResponse(contentId);
      if (savedResponse && savedResponse.responseContent) {
        try {
          const parsed = JSON.parse(savedResponse.responseContent);
          if (parsed.imageUrl) {
            const result = await saveImageCommentItem({
              project_section_content_id: contentId,
              image_url: parsed.imageUrl,
              comment: parsed.comment || '',
              position: 0,
            });
            setItems([{
              image_comment_item_id: result.image_comment_item_id,
              image_url: parsed.imageUrl,
              comment: parsed.comment || '',
              position: 0,
            }]);
          }
        } catch {
          // Not JSON or migration failed, ignore
        }
      }
    } catch (error) {
      console.error('Error migrating old data:', error);
    }
  };

  // Keep ref in sync
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Notify parent when items change
  useEffect(() => {
    const isValid = items.length > 0 && items.every(i => i.image_url && i.comment.trim());
    const response = JSON.stringify(items.map(i => ({
      imageUrl: i.image_url,
      comment: i.comment.trim(),
    })));
    onResponseChange?.(isValid, response);
  }, [items, onResponseChange]);

  // Keep commentIndex in bounds
  useEffect(() => {
    if (commentIndex >= items.length && items.length > 0) {
      setCommentIndex(items.length - 1);
    }
  }, [items.length, commentIndex]);

  // Scroll active thumbnail into view
  useEffect(() => {
    if (phase === 'comment' && thumbnailStripRef.current) {
      const activeThumb = thumbnailStripRef.current.children[commentIndex] as HTMLElement;
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [commentIndex, phase]);

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Formato no soportado. Usa JPG, PNG, GIF o WebP.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'La imagen supera el tamano maximo de 10MB.';
    }
    return null;
  };

  const handleUploadMultiple = useCallback(async (files: File[]) => {
    const available = maxImages - itemsRef.current.length;
    if (available <= 0) {
      setUploadError(`Has alcanzado el limite maximo de ${maxImages} imagenes`);
      return;
    }

    const filesToUpload = files.slice(0, available);
    if (filesToUpload.length < files.length) {
      setUploadError(`Solo se subiran ${filesToUpload.length} de ${files.length} imagenes (limite: ${maxImages})`);
    } else {
      setUploadError(null);
    }

    // Validate all files first
    for (const file of filesToUpload) {
      const error = validateFile(file);
      if (error) {
        setUploadError(error);
        return;
      }
    }

    setIsUploading(true);
    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        setUploadProgress(`Subiendo imagen ${i + 1} de ${filesToUpload.length}...`);
        const file = filesToUpload[i];
        const result = await uploadImage(file);
        const position = itemsRef.current.length;

        const saveResult = await saveImageCommentItem({
          project_section_content_id: contentId,
          image_url: result.url,
          comment: '',
          position,
        });

        setItems(prev => [...prev, {
          image_comment_item_id: saveResult.image_comment_item_id,
          image_url: result.url,
          comment: '',
          position,
        }]);
      }
    } catch (err: any) {
      setUploadError(err.message || 'Error al subir la imagen');
    } finally {
      setIsUploading(false);
      setUploadProgress('');
    }
  }, [maxImages, contentId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleUploadMultiple(Array.from(files));
    }
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleUploadMultiple(Array.from(files));
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    try {
      await deleteImageCommentItem(itemId);
      setItems(prev => prev.filter(i => i.image_comment_item_id !== itemId));
    } catch (err: any) {
      console.error('Error deleting item:', err);
    }
  };

  const handleCommentChange = (itemId: string, newComment: string) => {
    setItems(prev => prev.map(item =>
      item.image_comment_item_id === itemId
        ? { ...item, comment: newComment }
        : item
    ));

    if (debounceTimers.current[itemId]) {
      clearTimeout(debounceTimers.current[itemId]);
    }
    debounceTimers.current[itemId] = setTimeout(async () => {
      const item = itemsRef.current.find(i => i.image_comment_item_id === itemId);
      if (!item) return;
      try {
        await saveImageCommentItem({
          project_section_content_id: contentId,
          image_comment_item_id: itemId,
          image_url: item.image_url,
          comment: newComment,
          position: item.position,
        });
      } catch (err) {
        console.error('Error saving comment:', err);
      }
    }, 1000);
  };

  const handleTranscriptChange = (itemId: string, transcribedText: string) => {
    const item = itemsRef.current.find(i => i.image_comment_item_id === itemId);
    if (!item) return;
    const newComment = item.comment && item.comment.trim()
      ? `${item.comment} ${transcribedText}`
      : transcribedText;
    handleCommentChange(itemId, newComment);
  };

  // Cleanup debounce timers
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(clearTimeout);
    };
  }, []);

  const goToCommentPhase = () => {
    if (items.length > 0) {
      setCommentIndex(0);
      setPhase('comment');
    }
  };

  const goToGallery = () => {
    setPhase('gallery');
  };

  const goNext = () => {
    if (commentIndex < items.length - 1) {
      setCommentIndex(commentIndex + 1);
    } else {
      // Last image → back to gallery
      goToGallery();
    }
  };

  const goPrev = () => {
    if (commentIndex > 0) {
      setCommentIndex(commentIndex - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  const currentItem = items[commentIndex];

  // ==================== PHASE 2: COMMENT ====================
  if (phase === 'comment' && currentItem) {
    return (
      <motion.div
        initial={{ x: 300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -300, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="image-comment-content"
        style={{ maxWidth: '100%', width: '100%', padding: '0 2rem' }}
      >
        {/* Top bar: back button + thumbnail strip */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}>
          <button
            onClick={goToGallery}
            style={{
              background: 'none',
              border: '1px solid #ccc',
              borderRadius: '6px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.85rem',
              color: '#555',
              flexShrink: 0,
            }}
            title="Volver a galeria"
          >
            <i className="bi bi-grid-3x3-gap"></i>
          </button>

          {/* Thumbnail strip */}
          <div
            ref={thumbnailStripRef}
            style={{
              display: 'flex',
              gap: '6px',
              overflowX: 'auto',
              flex: 1,
              padding: '4px 0',
              scrollBehavior: 'smooth',
            }}
          >
            {items.map((item, idx) => {
              const hasComment = item.comment.trim().length > 0;
              const isActive = idx === commentIndex;
              return (
                <div
                  key={item.image_comment_item_id}
                  onClick={() => setCommentIndex(idx)}
                  style={{
                    position: 'relative',
                    flexShrink: 0,
                    width: '48px',
                    height: '48px',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: isActive
                      ? '3px solid var(--color-primary, #0d6efd)'
                      : hasComment
                        ? '2px solid #28a745'
                        : '2px solid #ccc',
                    opacity: isActive ? 1 : 0.7,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <img
                    src={item.image_url}
                    alt={`Imagen ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  {hasComment && (
                    <div style={{
                      position: 'absolute',
                      bottom: '1px',
                      right: '1px',
                      background: '#28a745',
                      borderRadius: '50%',
                      width: '14px',
                      height: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <i className="bi bi-check" style={{ color: 'white', fontSize: '0.6rem' }}></i>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Image left + Textarea right */}
        <div className="row g-3" style={{ marginBottom: '1rem' }}>
          {/* Image column */}
          <div className="col-md-5">
            <img
              src={currentItem.image_url}
              alt={`Imagen ${commentIndex + 1}`}
              style={{
                width: '100%',
                maxHeight: '400px',
                objectFit: 'contain',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
              }}
            />
          </div>

          {/* Comment column */}
          <div className="col-md-7">
            <div style={{ position: 'relative', height: '100%' }}>
              <textarea
                className="form-control"
                style={{
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  minHeight: '200px',
                  height: '100%',
                  resize: 'vertical',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  paddingBottom: '3rem',
                }}
                placeholder="Escribe tu comentario aqui..."
                value={currentItem.comment}
                onChange={(e) => handleCommentChange(currentItem.image_comment_item_id, e.target.value)}
              />
              <div style={{
                position: 'absolute',
                bottom: '12px',
                right: '12px',
                zIndex: 10,
              }}>
                <AudioTextInput
                  onTranscriptChange={(text) => handleTranscriptChange(currentItem.image_comment_item_id, text)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <button
            onClick={goPrev}
            disabled={commentIndex === 0}
            style={{
              fontSize: '3rem',
              width: '80px',
              height: '80px',
              border: 'none',
              background: 'none',
              color: 'var(--color-dark)',
              cursor: commentIndex === 0 ? 'not-allowed' : 'pointer',
              opacity: commentIndex === 0 ? 0.3 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="bi bi-chevron-left"></i>
          </button>

          <span style={{
            fontSize: '0.9rem',
            color: '#666',
            minWidth: '120px',
            textAlign: 'center',
          }}>
            Imagen {commentIndex + 1} de {items.length}
          </span>

          <button
            onClick={goNext}
            style={{
              fontSize: '3rem',
              width: '80px',
              height: '80px',
              border: 'none',
              background: 'none',
              color: 'var(--color-dark)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {commentIndex < items.length - 1 ? (
              <i className="bi bi-chevron-right"></i>
            ) : (
              <i className="bi bi-chevron-double-right"></i>
            )}
          </button>
        </div>
      </motion.div>
    );
  }

  // ==================== PHASE 1: GALLERY ====================
  return (
    <motion.div
      initial={{ x: navigationDirection === 'forward' ? 300 : -300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: navigationDirection === 'forward' ? -300 : 300, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="image-comment-content"
      style={{ maxWidth: '100%', width: '100%', padding: '0 2rem' }}
    >
      {/* Content text */}
      <div className="question-text mb-4" style={{ textAlign: 'left' }}>
        <div
          style={{ fontSize: '1.1rem', lineHeight: '1.8', margin: '0' }}
          dangerouslySetInnerHTML={{ __html: contentText }}
        />
      </div>

      {/* Image grid */}
      {items.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '12px',
          marginBottom: '1rem',
        }}>
          {items.map((item, idx) => {
            const hasComment = item.comment.trim().length > 0;
            return (
              <div
                key={item.image_comment_item_id}
                style={{
                  position: 'relative',
                  aspectRatio: '1',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: hasComment ? '3px solid #28a745' : '2px solid #ccc',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s ease',
                }}
                onClick={() => {
                  setCommentIndex(idx);
                  setPhase('comment');
                }}
              >
                <img
                  src={item.image_url}
                  alt={`Imagen ${idx + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />

                {/* Delete button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteItem(item.image_comment_item_id);
                  }}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    zIndex: 10,
                  }}
                  title="Eliminar"
                >
                  <i className="bi bi-x-lg"></i>
                </button>

                {/* Comment indicator */}
                {hasComment && (
                  <div style={{
                    position: 'absolute',
                    bottom: '4px',
                    right: '4px',
                    background: '#28a745',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <i className="bi bi-check" style={{ color: 'white', fontSize: '0.8rem' }}></i>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Drop zone / upload area */}
      {items.length < maxImages ? (
        <div
          onClick={() => !isUploading && fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${isDragOver ? 'var(--color-primary, #0d6efd)' : '#ccc'}`,
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            cursor: isUploading ? 'wait' : 'pointer',
            backgroundColor: isDragOver ? 'rgba(13,110,253,0.05)' : '#fafafa',
            transition: 'all 0.2s ease',
            minHeight: items.length === 0 ? '250px' : '100px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isUploading ? (
            <>
              <div className="spinner-border text-primary mb-2" role="status">
                <span className="visually-hidden">Subiendo imagen...</span>
              </div>
              <p style={{ color: '#666', margin: 0 }}>
                {uploadProgress || 'Subiendo imagen...'}
              </p>
            </>
          ) : (
            <>
              <i
                className="bi bi-plus-circle"
                style={{ fontSize: '2rem', color: '#aaa', marginBottom: '0.5rem' }}
              ></i>
              <p style={{ color: '#666', margin: 0, fontSize: '1rem' }}>
                {items.length === 0
                  ? 'Arrastra imagenes o haz clic para seleccionar'
                  : 'Anadir mas imagenes'}
              </p>
              <p style={{ color: '#999', margin: '0.3rem 0 0', fontSize: '0.8rem' }}>
                JPG, PNG, GIF o WebP (max. 10MB) — {items.length}/{maxImages}
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="text-center text-muted py-2" style={{ fontSize: '0.9rem' }}>
          Has alcanzado el limite maximo de {maxImages} imagenes
        </div>
      )}

      {uploadError && (
        <div className="alert alert-danger mt-2 py-2" style={{ fontSize: '0.9rem' }}>
          <i className="bi bi-exclamation-triangle me-1"></i>
          {uploadError}
        </div>
      )}

      {/* "Comentar imagenes" button */}
      {items.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
          <button
            onClick={goToCommentPhase}
            className="btn btn-primary"
            style={{
              padding: '0.6rem 2rem',
              fontSize: '1rem',
            }}
          >
            <i className="bi bi-chat-left-text me-2"></i>
            Comentar imagenes
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </motion.div>
  );
};

export default ImageCommentViewer;
